package pl.mothdigital.neuronowi.pages.about

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobweb.silk.components.graphics.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.text.*
import com.varabyte.kobweb.silk.style.*
import com.varabyte.kobweb.silk.style.breakpoint.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.keywords.*
import org.jetbrains.compose.web.dom.*
import pl.mothdigital.neuronowi.common.resources.StringResources
import pl.mothdigital.neuronowi.common.utils.aspectRatio
import pl.mothdigital.neuronowi.common.utils.loading
import pl.mothdigital.neuronowi.common.utils.fixPolishOrphans
import pl.mothdigital.neuronowi.components.layouts.*
import pl.mothdigital.neuronowi.components.text.*
import pl.mothdigital.neuronowi.components.widgets.*
import pl.mothdigital.neuronowi.navigation.*
import pl.mothdigital.neuronowi.theme.*
import pl.mothdigital.neuronowi.theme.text.*

@Page
@Composable
fun AboutPage() {
    val ctx = rememberPageContext()

    PageLayout(
        title = StringResources.ABOUT_PAGE_TITLE,
        description = StringResources.META_DESC_ABOUT,
        ogImage = "/about_section.webp",
        contentAlignment = Alignment.TopStart,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.Top,
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            VerticalSpacer(78.px)
            AboutHeaderSection()
            AboutStorySection()
            AboutHowWeWorkSection()
            AboutTeamEssenceSection()
            AboutWhyUsSection()
            AboutQuoteSection()
            AboutCTASection(ctx)
        }
    }
}

@Composable
fun AboutHeaderSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 4.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50)
            .toAttrs()
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().alignItems(AlignItems.Center),
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            AppH1(color = SiteTheme.green._900) { Text(StringResources.ABOUT_PAGE_TITLE) }
            AppP(color = SiteTheme.green._800) {
                Text(StringResources.ABOUT_HEADER_SUBTITLE.fixPolishOrphans())
            }
        }
    }
}

@Composable
fun AboutStorySection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 4.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .flexWrap(FlexWrap.WrapReverse)
                    .alignItems(AlignItems.Center)
                    .displayIfAtLeast(Breakpoint.MD),
        ) {
            Column(
                modifier = Modifier
                    .flex(1)
                    .minWidth(400.px)
                    .padding(1.cssRem)
            ) {
                AppH2 { Text(StringResources.ABOUT_STORY_TITLE) }
                VerticalSpacer(1.cssRem)
                AppP { Text(StringResources.ABOUT_STORY_DESC_1.fixPolishOrphans()) }
                VerticalSpacer(1.cssRem)
                AppP { Text(StringResources.ABOUT_STORY_DESC_2.fixPolishOrphans()) }
            }
            Column(
                modifier = Modifier
                    .flex(1)
                    .minWidth(400.px)
                    .padding(1.cssRem)
            ) {
                Image(
                    src = "/about_story.webp",
                    alt = StringResources.ABOUT_STORY_IMAGE_ALT,
                    modifier = Modifier
                        .maxWidth(100.percent)
                        .height(auto)
                        .aspectRatio("3/2")
                        .loading()
                        .borderRadius(1.cssRem)
                        .boxShadow(blurRadius = 10.px, color = rgba(0, 0, 0, 0.06))
                )
            }
        }
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .alignItems(AlignItems.Center)
                    .displayUntil(Breakpoint.MD),
        ) {
            Column(
                modifier = Modifier
                    .flex(1)
                    .minWidth(300.px)
                    .padding(1.cssRem)
            ) {
                Image(
                    src = "/about_story.webp",
                    alt = StringResources.ABOUT_STORY_IMAGE_ALT,
                    modifier = Modifier
                        .maxWidth(100.percent)
                        .height(auto)
                        .aspectRatio("3/2")
                        .loading()
                        .borderRadius(1.cssRem)
                        .boxShadow(blurRadius = 10.px, color = rgba(0, 0, 0, 0.06))
                )
            }
            Column(
                modifier = Modifier
                    .flex(1)
                    .minWidth(300.px)
                    .padding(1.cssRem)
            ) {
                AppH2 { Text(StringResources.ABOUT_STORY_TITLE) }
                VerticalSpacer(1.cssRem)
                AppP { Text(StringResources.ABOUT_STORY_DESC_1.fixPolishOrphans()) }
                VerticalSpacer(1.cssRem)
                AppP { Text(StringResources.ABOUT_STORY_DESC_2.fixPolishOrphans()) }
            }
        }
    }
}

@Composable
fun AboutHowWeWorkSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 4.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50)
            .toAttrs()
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().alignItems(AlignItems.Center),
            verticalArrangement = Arrangement.spacedBy(2.cssRem)
        ) {
            AppH2 { Text(StringResources.ABOUT_HOW_WE_WORK_TITLE) }

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .flexWrap(FlexWrap.Wrap)
                    .gap(2.cssRem)
                    .justifyContent(com.varabyte.kobweb.compose.css.JustifyContent.Center)
            ) {
                WorkItem(
                    icon = { FaComments() },
                    title = StringResources.ABOUT_WORK_ITEM_1_TITLE,
                    text = StringResources.ABOUT_WORK_ITEM_1_DESC
                )
                WorkItem(
                    icon = { FaUserCheck() },
                    title = StringResources.ABOUT_WORK_ITEM_2_TITLE,
                    text = StringResources.ABOUT_WORK_ITEM_2_DESC
                )
                WorkItem(
                    icon = { FaHandsHolding() },
                    title = StringResources.ABOUT_WORK_ITEM_3_TITLE,
                    text = StringResources.ABOUT_WORK_ITEM_3_DESC
                )
            }
        }
    }
}

@Composable
private fun WorkItem(icon: @Composable () -> Unit, title: String, text: String) {
    Column(
        modifier = Modifier
            .width(300.px)
            .padding(1.cssRem)
            .backgroundColor(SiteTheme.white._900)
            .borderRadius(1.cssRem)
            .boxShadow(blurRadius = 6.px, color = rgba(0, 0, 0, 0.05)),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(0.7.cssRem)
    ) {
        icon().apply {
            Modifier
                .fontSize(2.cssRem)
                .color(SiteTheme.green._800)
        }

        AppH3(modifier = Modifier.textAlign(TextAlign.Center)) { Text(title.fixPolishOrphans()) }

        AppP(modifier = Modifier.textAlign(TextAlign.Center)) { Text(text.fixPolishOrphans()) }
    }
}

@Composable
fun AboutTeamEssenceSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 4.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .alignItems(AlignItems.Center)
                .textAlign(TextAlign.Center),
            verticalArrangement = Arrangement.spacedBy(2.cssRem)
        ) {
            AppH2 { Text(StringResources.ABOUT_TEAM_ESSENCE_TITLE) }

            AppP(modifier = Modifier.maxWidth(700.px)) {
                Text(StringResources.ABOUT_TEAM_ESSENCE_DESC.fixPolishOrphans())
            }

            Image(
                src = "/about_section.webp",
                alt = StringResources.ABOUT_TEAM_IMAGE_ALT,
                modifier = Modifier
                    .maxWidth(60.percent)
                    .widthIn(min = 320.px)
                    .aspectRatio("3/2")
                    .loading()
                    .borderRadius(1.cssRem)
                    .boxShadow(blurRadius = 10.px, color = rgba(0, 0, 0, 0.06))
            )
        }
    }
}

@Composable
fun AboutQuoteSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 4.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().alignItems(AlignItems.Center).textAlign(TextAlign.Center),
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            AppP {
                Text(StringResources.ABOUT_QUOTE_TEXT.fixPolishOrphans())
            }

            AppP(modifier = Modifier.fontWeight(FontWeight.Medium)) {
                Text(StringResources.ABOUT_QUOTE_AUTHOR.fixPolishOrphans())
            }
        }
    }
}

@Composable
fun AboutWhyUsSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 4.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50)
            .toAttrs()
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .alignItems(AlignItems.Center),
            verticalArrangement = Arrangement.spacedBy(2.cssRem)
        ) {
            AppH2 { Text(StringResources.ABOUT_WHY_US_TITLE) }

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .flexWrap(FlexWrap.Wrap)
                    .gap(2.cssRem)
                    .justifyContent(com.varabyte.kobweb.compose.css.JustifyContent.Center)
            ) {
                WhyUsItem(
                    icon = { FaUsers() },
                    title = StringResources.ABOUT_WHY_ITEM_1_TITLE,
                    description = StringResources.ABOUT_WHY_ITEM_1_DESC
                )
                WhyUsItem(
                    icon = { FaCalendarCheck() },
                    title = StringResources.ABOUT_WHY_ITEM_2_TITLE,
                    description = StringResources.ABOUT_WHY_ITEM_2_DESC
                )
                WhyUsItem(
                    icon = { FaChild() },
                    title = StringResources.ABOUT_WHY_ITEM_3_TITLE,
                    description = StringResources.ABOUT_WHY_ITEM_3_DESC
                )
                WhyUsItem(
                    icon = { FaHandsHolding() },
                    title = StringResources.ABOUT_WHY_ITEM_4_TITLE,
                    description = StringResources.ABOUT_WHY_ITEM_4_DESC
                )
                WhyUsItem(
                    icon = { FaFaceSmile() },
                    title = StringResources.ABOUT_WHY_ITEM_5_TITLE,
                    description = StringResources.ABOUT_WHY_ITEM_5_DESC
                )
                WhyUsItem(
                    icon = { FaUserTie() },
                    title = StringResources.ABOUT_WHY_ITEM_6_TITLE,
                    description = StringResources.ABOUT_WHY_ITEM_6_DESC
                )
                WhyUsItem(
                    icon = { FaBaby() },
                    title = StringResources.ABOUT_WHY_ITEM_7_TITLE,
                    description = StringResources.ABOUT_WHY_ITEM_7_DESC
                )
            }
        }
    }
}

@Composable
private fun WhyUsItem(
    icon: @Composable () -> Unit,
    title: String,
    description: String
) {
    Column(
        modifier = Modifier
            .width(260.px)
            .height(220.px)
            .padding(1.cssRem)
            .backgroundColor(SiteTheme.white._900)
            .borderRadius(1.cssRem)
            .boxShadow(blurRadius = 6.px, color = rgba(0, 0, 0, 0.05)),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(0.5.cssRem)
    ) {
        icon().apply {
            Modifier
                .fontSize(2.cssRem)
                .color(SiteTheme.green._800)
        }

        AppH3(modifier = Modifier.textAlign(TextAlign.Center)) { Text(title.fixPolishOrphans()) }

        AppP(modifier = Modifier.textAlign(TextAlign.Center)) { Text(description.fixPolishOrphans()) }
    }
}

@Composable
fun AboutCTASection(ctx: PageContext) {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .backgroundColor(SiteTheme.green._100)
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .alignItems(com.varabyte.kobweb.compose.css.AlignItems.Center)
                .textAlign(TextAlign.Center),
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            AppH2(color = SiteTheme.green._900) {
                Text(StringResources.ABOUT_CTA_TITLE.fixPolishOrphans())
            }

            AppP(color = SiteTheme.green._800) {
                Text(StringResources.ABOUT_CTA_DESC.fixPolishOrphans())
            }

            com.varabyte.kobweb.silk.components.forms.Button(
                onClick = { ctx.router.navigateTo(Screen.Contact.route) },
                modifier = Modifier.padding(8.px),
                type = ButtonType.Button,
                colorPalette = SiteTheme.green,
            ) {
                SpanText(
                    text = StringResources.ABOUT_CTA_BUTTON,
                    modifier = TextBaseStyle.toModifier()
                        .color(SiteTheme.white._900)
                        .fontWeight(FontWeight.Normal)
                )
            }
        }
    }
}
